import { useMemo, useState, useCallback, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, ArrowUp, ArrowDown, FileText, RotateCcw, Trash2, Send, AlertTriangle, Plus, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { api } from "@/lib/api";
import NotesThread from "@/components/NotesThread";
import { ContactCell } from "@/components/ContactCell";
import PersonPick from "@/components/PersonPick";
import DirectoryContactPicker from "@/components/DirectoryContactPicker";
import { ContractStatusBadge, CONTRACT_ROW_COLORS } from "@/components/ContractStatusBadge";

const STATUS_OPTIONS = ["Open", "Reviewed", "Negotiating", "Pending CEO Signature", "Pending GC Signature", "Handoff Requested", "Closed"];
const TYPE_OPTIONS = ["Contract", "LOI"];

function SortHeader({ column, label }) {
  const sorted = column.getIsSorted();
  const Icon = sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ChevronsUpDown;
  return (
    <button className={`inline-flex items-center gap-1 hover:text-foreground ${sorted ? "text-foreground" : ""}`}
            onClick={() => column.toggleSorting(sorted === "asc")} data-testid={`contract-sort-header-${column.id}`}>
      {label}<Icon className={`h-3 w-3 ${sorted ? "" : "opacity-40"}`} />
    </button>
  );
}

export function ContractsTable({ data, globalFilter, onUpdateContract, onReplaceContract, isArchiveView, onUnarchive, onDelete, onFollowUp }) {
  const [sorting, setSorting] = useState([]);
  const [editingCell, setEditingCell] = useState(null);

  // Parent callbacks via a ref so the column defs (and open note popovers) survive parent re-renders.
  const cb = useRef({});
  cb.current = { onUpdateContract, onReplaceContract, onUnarchive, onDelete, onFollowUp };
  const handleInlineUpdate = useCallback((contractId, field, value) => {
    cb.current.onUpdateContract(contractId, { [field]: value });
    setEditingCell(null);
  }, []);

  const columns = useMemo(
    () => [
      // JOB #
      {
        accessorKey: "job_number",
        header: ({ column }) => <SortHeader column={column} label="Job #" />,
        cell: ({ row }) => {
          const contract = row.original;
          const isEditing = editingCell?.rowId === contract.id && editingCell?.field === "job_number";
          if (isEditing) {
            return (
              <Input
                defaultValue={contract.job_number || ""}
                autoFocus
                className="h-7 text-xs w-24"
                data-testid={`job-number-input-${row.index}`}
                onBlur={(e) => handleInlineUpdate(contract.id, "job_number", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInlineUpdate(contract.id, "job_number", e.target.value);
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          return (
            <div
              className="text-xs text-slate-700 font-medium cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
              onClick={() => setEditingCell({ rowId: contract.id, field: "job_number" })}
              data-testid={`cell-job-number-${row.index}`}
            >
              {contract.job_number || <span className="text-slate-400 italic">Set #</span>}
            </div>
          );
        },
        size: 60,
      },
      // JOB NAME
      {
        accessorKey: "job_name",
        header: ({ column }) => <SortHeader column={column} label="Name" />,
        cell: ({ row }) => {
          const contract = row.original;
          const isEditing = editingCell?.rowId === contract.id && editingCell?.field === "job_name";
          if (isEditing) {
            return (
              <Input
                defaultValue={contract.job_name || ""}
                autoFocus
                className="h-7 text-xs w-full"
                data-testid={`job-name-input-${row.index}`}
                onBlur={(e) => handleInlineUpdate(contract.id, "job_name", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInlineUpdate(contract.id, "job_name", e.target.value);
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          return (
            <div
              className="font-medium text-slate-900 truncate max-w-[130px] cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
              onClick={() => setEditingCell({ rowId: contract.id, field: "job_name" })}
              data-testid={`cell-job-name-${row.index}`}
              title={contract.job_name}
            >
              {contract.job_name || <span className="text-slate-400 italic">Set name</span>}
            </div>
          );
        },
        size: 145,
      },
      // CUSTOMER
      {
        accessorKey: "customer",
        header: ({ column }) => <SortHeader column={column} label="Customer" />,
        cell: ({ row }) => {
          const contract = row.original;
          const isEditing = editingCell?.rowId === contract.id && editingCell?.field === "customer";
          if (isEditing) {
            return (
              <Input
                defaultValue={contract.customer || ""}
                autoFocus
                className="h-7 text-xs w-full"
                data-testid={`customer-input-${row.index}`}
                onBlur={(e) => handleInlineUpdate(contract.id, "customer", e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInlineUpdate(contract.id, "customer", e.target.value);
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          return (
            <div
              className="text-xs text-slate-700 truncate max-w-[90px] cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
              onClick={() => setEditingCell({ rowId: contract.id, field: "customer" })}
              data-testid={`cell-customer-${row.index}`}
              title={contract.customer}
            >
              {contract.customer || <span className="text-slate-400 italic">Set customer</span>}
            </div>
          );
        },
        size: 100,
      },
      // CONTACT — name over email (Pipeline pattern); "+" / search icon pick from the Directory or add a new contact to it
      {
        accessorKey: "contact_name",
        header: ({ column }) => <SortHeader column={column} label="Contact" />,
        cell: ({ row }) => {
          const c = row.original;
          const save = (d) => cb.current.onUpdateContract(c.id, { contact_name: d.name, contact_email: d.email,
                                                                    ...(d.phone !== undefined ? { contact_phone: d.phone } : {}) });
          const picker = (trigger) => (
            <DirectoryContactPicker companyName={c.customer || ""} testid={`contract-contact-${row.index}-dir`} onPick={save}>{trigger}</DirectoryContactPicker>
          );
          const flag = c.contact_missing && !isArchiveView && (
            <span title="Contact and email required to send email" className="mt-0.5 shrink-0 cursor-help" data-testid={`contact-flag-${row.index}`}>
              <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            </span>);
          if (!c.contact_name && !c.contact_email) {
            return (
              <div className="flex items-center gap-1.5">{flag}{picker(
                <button type="button" title="Add contact from the Directory" data-testid={`contract-contact-${row.index}-add`}
                        className="py-1 text-muted-foreground hover:text-[#007BFF]"><Plus className="h-3.5 w-3.5" /></button>)}</div>);
          }
          return (
            <div className="group/ct flex items-start gap-1" title={c.contact_override ? "Contract-specific contact (Pipeline sync off). Clear it to re-sync from Pipeline." : "From Pipeline"}>
              {flag}
              <ContactCell name={c.contact_name || ""} email={c.contact_email || ""} testid={`contract-contact-${row.index}`}
                           directory={{ companyName: c.customer || "", type: "gc" }} onSave={save} />
              {picker(
                <button type="button" title="Pick from the Directory" data-testid={`contract-contact-${row.index}-pick`}
                        className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 opacity-0 transition-opacity hover:text-[#007BFF] group-hover/ct:opacity-100"><Search className="h-3 w-3" /></button>)}
            </div>
          );
        },
        size: 190,
      },
      // PHONE — the contact's phone (from Pipeline unless the contract pins its own)
      {
        accessorKey: "contact_phone",
        header: ({ column }) => <SortHeader column={column} label="Phone" />,
        cell: ({ row }) => {
          const c = row.original;
          const isEditing = editingCell?.rowId === c.id && editingCell?.field === "contact_phone";
          if (isEditing) {
            return <input autoFocus defaultValue={c.contact_phone || ""} placeholder="Phone" type="tel" data-testid={`contract-phone-input-${row.index}`}
                          className="w-28 border-0 border-b border-[#007BFF] bg-transparent py-0.5 text-xs outline-none"
                          onBlur={(e) => { const v = e.target.value.trim(); if (v !== (c.contact_phone || "")) handleInlineUpdate(c.id, "contact_phone", v); else setEditingCell(null); }}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") setEditingCell(null); }} />;
          }
          return (
            <div className="min-h-[20px] cursor-text text-xs" data-testid={`contract-phone-${row.index}`}
                 onClick={() => setEditingCell({ rowId: c.id, field: "contact_phone" })}>
              {c.contact_phone ? <a href={`tel:${c.contact_phone.replace(/[^\d+]/g, "")}`} onClick={(e) => e.stopPropagation()} className="text-sky-700 hover:underline dark:text-sky-400">{c.contact_phone}</a>
                               : <span className="text-muted-foreground">—</span>}
            </div>
          );
        },
        size: 120,
      },
      // ACCOUNT MANAGER
      {
        accessorKey: "account_manager",
        header: ({ column }) => <SortHeader column={column} label="AM" />,
        cell: ({ row }) => {
          const contract = row.original;
          return <PersonPick value={contract.account_manager} kind="am" testid={`cell-contract-am-${row.index}`}
                             onChange={(v) => handleInlineUpdate(contract.id, "account_manager", v)} />;
        
        },
        size: 48,
      },
      // TYPE
      {
        accessorKey: "contract_type",
        header: ({ column }) => <SortHeader column={column} label="Type" />,
        cell: ({ row }) => {
          const contract = row.original;
          return (
            <div data-testid={`cell-contract-type-${row.index}`}>
              <Select
                value={contract.contract_type || "Contract"}
                onValueChange={(val) => handleInlineUpdate(contract.id, "contract_type", val)}
              >
                <SelectTrigger
                  className="h-7 border-0 bg-transparent p-0 shadow-none focus:ring-0 w-auto text-xs"
                  data-testid={`contract-type-select-${row.index}`}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`contract-type-option-${t.toLowerCase()}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        },
        size: 72,
      },
      // RECEIVED
      {
        accessorKey: "received",
        header: ({ column }) => <SortHeader column={column} label="Received" />,
        cell: ({ row }) => {
          const contract = row.original;
          const isEditing = editingCell?.rowId === contract.id && editingCell?.field === "received";
          if (isEditing) {
            return (
              <Input
                type="date"
                defaultValue={contract.received || ""}
                autoFocus
                className="h-7 text-xs w-36"
                data-testid={`received-input-${row.index}`}
                onBlur={(e) => handleInlineUpdate(contract.id, "received", e.target.value || null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleInlineUpdate(contract.id, "received", e.target.value || null);
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          const val = contract.received;
          if (!val) {
            return (
              <div
                className="text-slate-400 italic text-xs cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
                onClick={() => setEditingCell({ rowId: contract.id, field: "received" })}
                data-testid={`cell-received-${row.index}`}
              >
                Set date
              </div>
            );
          }
          const d = new Date(val + "T00:00:00");
          return (
            <div
              className="text-slate-600 text-xs cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
              onClick={() => setEditingCell({ rowId: contract.id, field: "received" })}
              data-testid={`cell-received-${row.index}`}
            >
              {d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" })}
            </div>
          );
        },
        size: 80,
      },
      // STATUS
      {
        accessorKey: "status",
        header: ({ column }) => <SortHeader column={column} label="Status" />,
        cell: ({ row }) => {
          const contract = row.original;
          return (
            <div data-testid={`cell-contract-status-${row.index}`}>
              <Select
                value={contract.status}
                onValueChange={(val) => handleInlineUpdate(contract.id, "status", val)}
              >
                <SelectTrigger
                  className="h-7 border-0 bg-transparent p-0 shadow-none focus:ring-0 w-auto"
                  data-testid={`contract-status-select-${row.index}`}
                >
                  <SelectValue>
                    <ContractStatusBadge status={contract.status} />
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`contract-status-option-${s.toLowerCase().replace(/\s+/g, '-')}`}>
                      <ContractStatusBadge status={s} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        },
        size: 150,
      },
      // CONTRACT VALUE
      {
        accessorKey: "contract_value",
        header: ({ column }) => <SortHeader column={column} label="Contract Value" />,
        cell: ({ row }) => {
          const contract = row.original;
          const isEditing = editingCell?.rowId === contract.id && editingCell?.field === "contract_value";
          if (isEditing) {
            return (
              <Input
                type="number"
                defaultValue={contract.contract_value ?? ""}
                autoFocus
                className="h-7 text-xs w-28"
                data-testid={`contract-value-input-${row.index}`}
                onBlur={(e) => {
                  const val = e.target.value ? parseFloat(e.target.value) : null;
                  handleInlineUpdate(contract.id, "contract_value", val);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const val = e.target.value ? parseFloat(e.target.value) : null;
                    handleInlineUpdate(contract.id, "contract_value", val);
                  }
                  if (e.key === "Escape") setEditingCell(null);
                }}
              />
            );
          }
          const val = contract.contract_value;
          return (
            <div
              className="text-xs text-slate-700 font-medium cursor-pointer hover:text-[#007BFF] hover:bg-blue-50/50 rounded px-1 py-0.5 -mx-1 transition-colors"
              onClick={() => setEditingCell({ rowId: contract.id, field: "contract_value" })}
              data-testid={`cell-contract-value-${row.index}`}
            >
              {val != null
                ? `$${val.toLocaleString("en-US", { maximumFractionDigits: 0 })}`
                : <span className="text-slate-400 italic">Set value</span>}
            </div>
          );
        },
        size: 95,
      },
      // AGE (days since contract created)
      {
        accessorKey: "age_days",
        header: ({ column }) => <SortHeader column={column} label="Age" />,
        cell: ({ row }) => {
          const c = row.original;
          return (
            <div data-testid={`cell-age-${row.index}`}>
              <span className="text-xs font-semibold text-slate-900 tabular-nums">
                {c.age_days != null ? `${c.age_days}d` : "—"}
              </span>
              {c.stall_days != null && (
                <div
                  className={`text-[10px] ${c.flag_red ? "text-red-600 font-semibold" : "text-slate-400"}`}
                  title={c.flag_reason || undefined}
                >
                  stall {c.stall_days}d
                </div>
              )}
            </div>
          );
        },
        size: 48,
      },
      // REMINDERS
      {
        accessorKey: "reminder_count",
        header: ({ column }) => <SortHeader column={column} label="Reminders" />,
        cell: ({ row }) => {
          const c = row.original;
          const history = (c.auto_notes || [])
            .slice()
            .reverse()
            .map((n) => {
              const d = n.ts ? new Date(n.ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";
              return `${d} · ${n.kind || "auto"} — ${n.text}`;
            })
            .join("\n");
          const lastDate = c.last_reminder_sent_at
            ? new Date(c.last_reminder_sent_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })
            : null;
          return (
            <div
              data-testid={`cell-reminders-${row.index}`}
              className="cursor-help"
              title={history || "No reminders sent yet"}
            >
              <div className="text-xs font-medium text-slate-700">
                #{c.reminder_count || 0} sent{lastDate ? <span className="text-slate-400 font-normal"> · {lastDate}</span> : null}
              </div>
              {!isArchiveView && c.next_reminder_hint && (
                c.reminder_due ? (
                  <div className="text-[10px] font-semibold text-amber-600" data-testid={`reminder-due-${row.index}`}>Due now</div>
                ) : (
                  <div className="text-[10px] text-slate-400 truncate max-w-[85px]">{c.next_reminder_hint}</div>
                )
              )}
            </div>
          );
        },
        size: 105,
      },
      // NOTES — shared NotesThread (UI_PATTERNS.md › Notes UI); a legacy free-text note shows as the oldest entry
      {
        accessorKey: "notes",
        header: () => <span>Notes</span>,
        cell: ({ row }) => {
          const c = row.original;
          const notes = [...(c.note_log || [])].reverse();
          if (c.notes) notes.push({ text: c.notes, user: "", initials: "", at: null });
          return (
            <NotesThread notes={notes} title={c.job_name} testid={`contract-notes-${c.id}`} target={{ contract_id: c.id }}
                         onAdd={async (text) => {
                           try { cb.current.onReplaceContract?.(await api.addContractNote(c.id, text)); return true; }
                           catch (e) { toast.error(e?.response?.data?.detail || "Note not saved"); return false; }
                         }} />
          );
        },
        size: 70,
      },
      // FOLLOW-UP (active view only)
      ...(!isArchiveView ? [{
        id: "followup",
        header: () => <span>Follow-Up</span>,
        cell: ({ row }) => {
          const c = row.original;
          const disabled = !!c.contact_missing;
          return (
            <span title={disabled ? "Contact and email required to send email" : "Send follow-up email"} className="inline-block">
              <Button
                size="sm"
                variant="outline"
                className="h-7 w-9 p-0 text-xs"
                disabled={disabled}
                onClick={() => cb.current.onFollowUp(c)}
                aria-label="Send follow-up"
                data-testid={`followup-btn-${row.index}`}
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </span>
          );
        },
        size: 60,
      }] : []),
      // EXECUTED DATE (archive view only)
      ...(isArchiveView ? [{
        accessorKey: "executed_date",
        header: ({ column }) => <SortHeader column={column} label="Executed" />,
        cell: ({ row }) => {
          const val = row.original.executed_date;
          if (!val) {
            return <span className="text-slate-400 italic text-xs" data-testid={`cell-executed-${row.index}`}>--</span>;
          }
          const d = new Date(val + "T00:00:00");
          return (
            <span className="text-slate-600 text-xs" data-testid={`cell-executed-${row.index}`}>
              {d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}
            </span>
          );
        },
        size: 120,
      }] : []),
      // ACTIONS (archive view only)
      ...(isArchiveView ? [{
        id: "actions",
        header: () => <span>Actions</span>,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs text-[#007BFF] border-[#007BFF]/30 hover:bg-[#007BFF]/10"
              onClick={() => cb.current.onUnarchive(row.original.id)}
              data-testid={`contract-restore-button-${row.index}`}
            >
              <RotateCcw className="h-3 w-3" />
              Restore
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-xs text-red-500 border-red-200 hover:bg-red-50"
              onClick={() => cb.current.onDelete(row.original.id)}
              data-testid={`contract-delete-button-${row.index}`}
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </Button>
          </div>
        ),
        size: 200,
      }] : []),
    ],
    [editingCell, handleInlineUpdate, isArchiveView]
  );

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="flex-1 overflow-auto" data-testid="contracts-table-container">
      <Table className="text-xs">
        <TableHeader className="sticky top-0 z-10 bg-slate-50">
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id} className="border-b border-slate-200 hover:bg-transparent">
              {hg.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="h-auto text-[11px] font-semibold text-muted-foreground px-2 py-2 whitespace-nowrap"
                  style={{ width: header.getSize() }}
                  data-testid={`contract-column-header-${header.id}`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map((row) => {
              const rowColor = CONTRACT_ROW_COLORS[row.original.status] || "";
              return (
                <TableRow
                  key={row.id}
                  className={cn(
                    "group transition-colors border-b border-slate-100",
                    rowColor || "hover:bg-slate-50/80"
                  )}
                  data-testid={`contract-row-${row.index}`}
                  data-mention-row={`contract-${row.original.id}`}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="px-2 py-2" style={{ width: cell.column.getSize() }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-48 text-center">
                <div className="flex flex-col items-center gap-3" data-testid="contracts-empty-state">
                  <div className="h-14 w-14 rounded-xl bg-slate-100 flex items-center justify-center">
                    <FileText className="h-7 w-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600">
                      {isArchiveView ? "No archived contracts" : "No contracts yet"}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {isArchiveView
                        ? "Contracts with Closed status will appear here"
                        : "Contracts are auto-created when a pipeline project is marked as Won"}
                    </p>
                  </div>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
